<template>
    <div class="resolve-time-by-user-chart" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>{{ $t("report.averageResolveTimeByAgentsTitle") }}</h2>
        </div>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <div class="chart-wrapper">
            <EChartsHorizontalBar
                :labels="getLabels()"
                :datasets="getDatasets()"
                :height="320"
                v-if="!tableMode"
            />
            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>

        <div class="stats">
            <StatCard :title="$t('report.fastestSolver')" :value="getStats().MinAgent" />
            <StatCard :title="$t('report.slowestSolver')" :value="getStats().MaxAgent" />
            <StatCard :title="$t('report.averageSolutionTime')" :value="getStats().AvgAgent" unit="min" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import log2 from "../../../utils/log2";
import EChartsHorizontalBar from "../../../components/charts/EChartsHorizontalBar.vue";
import { mapState } from "vuex";
import fscreen from "fscreen";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";

const log = log2("ResolveTimeByUser");
export default Vue.extend({
    name: "ResolveTimeByUser",
    components: { ChartDoddleIcon, EChartsHorizontalBar, TableView, IconButton, StatCard },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
        loading: {
            type: Boolean,
            default: true,
        },
        data: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            stats: {
                MaxAgent: 0,
                MinAgent: 0,
                AvgAgent: 0,
            },
            tableMode: false,
            loadingInstance: null,
            fullScreen: false,
            cancels: [],
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
        }),
    },
    methods: {
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".resolve-time-by-user-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: this.$t('report.user'),
                    dataIndex: "user",
                    key: "user",
                },
                {
                    title: this.$t('report.resolveTime'),
                    dataIndex: "resolveTime",
                    key: "resolveTime",
                },
            ];
        },
        getRows() {
            let items = this.data || [];
            items = items.map((x) => {
                x.user = this.users.find((u) => u._id === x._id);
                return x;
            });

            log("getRows.items", items);
            const rows = items.map((item) => {
                return {
                    user: item.user?.name || "Unknown User",
                    resolveTime: item.avgResolveTime,
                };
            });
            return rows;
        },
        getStats() {
            let items = this.data || [];
            items = items.map((x) => {
                x.user = this.users.find((u) => u._id === x._id);
                return x;
            });
            const MaxAgent = _.chain(items).maxBy("avgResolveTime").value()?.user?.name || this.$t('report.noEnoughtData');
            const MinAgent = _.chain(items).minBy("avgResolveTime").value()?.user?.name || this.$t('report.noEnoughtData');
            const AvgAgent = parseInt(_.meanBy(items, "avgResolveTime") || 0);

            return {
                MaxAgent,
                MinAgent,
                AvgAgent,
            };
        },
        getLabels() {
            const items = this.data || [];
            return items.map((user) => {
                const userObj = this.users.find((u) => u._id === user._id);
                return userObj ? userObj.name : "Unknown User";
            });
        },
        getDatasets() {
            const items = this.data || [];
            const palette = [
                "#F59E0B", "#3B82F6", "#10B981", "#EF4444", "#8B5CF6",
                "#EC4899", "#06B6D4", "#F97316", "#6366F1", "#14B8A6",
                "#84CC16", "#E11D48", "#0EA5E9", "#D946EF", "#78716C",
            ];
            log("getDatasets.items", items);
            return [
                {
                    label: "Avg Resolve Time (min)",
                    data: items.map((item) => item.avgResolveTime),
                    backgroundColor: items.map((_, i) => palette[i % palette.length] + "B3"),
                    borderColor: items.map((_, i) => palette[i % palette.length]),
                    borderWidth: 1,
                    borderRadius: 4,
                },
            ];
        },
        exportToExcel() {
            let items = this.data || [];
            items = items.map((x) => {
                x.user = this.users.find((u) => u._id === x._id);
                return x;
            });
            log("exportToExcel.items", items);

            const excelData = items.map((item) => {
                return {
                    User: item.user?.name || "Unknown User",
                    "Avg Resolve Time": item.avgResolveTime,
                };
            });
            expertToExcel(excelData, `chat_session_count_by_users.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".resolve-time-by-user-chart"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.resolve-time-by-user-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.resolve-time-by-user-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
